import { GridColDef, GridFilterInputValueProps, getGridStringOperators } from "@mui/x-data-grid";
import { IBooking, IOrderHead, IOrderStyle } from "../models/order";
import { colorVariables } from "../../../utils/colorVariable";
import { Box, FormControl, InputLabel, MenuItem, Select,Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  RenderCustomer,
  RenderFactory,
  RenderInvoice,
  RenderMerchandiser,
  RenderShipmentMode,
  RenderSalesPeople,
  RenderShipmentTerm,
  RenderStatus,
  RenderMachine,
} from "../../../components/common/CellRender/RenderCellEdit";
import numeral from "numeral";
import { EORDERHEAD_STATUS } from "../../../utils/enumVariable";
import dayjs from "dayjs";


type Status = "New" | "Shipped" | "Canceled"|"Allocated"|"Partly Shipped"|"Confirmed";
const statusOptions: Status[] = ["New", "Shipped", "Canceled","Allocated","Confirmed","Partly Shipped"];
type Factory  = "AK" | "FK" ;
const factoryOptions: Factory[] = ["AK", "FK"];
const shipmentMethodOptions = ["AIR", "SEA", "LOCAL"] as const;
const shipmentTermOptions = [
  "FOB",
  "CFR",
  "FCA",
  "CIF",
  "EXW",
  "CPT",
  "DDP",
  "CIP",
  "DAP",
  "FAS",
  "DPU",
] as const;
 

const SelectFilterInput = ({
  props,
  options,
}: {
  props: GridFilterInputValueProps;
  options: ReadonlyArray<{ label: string; value: string }>;
}) => {
  const { item, applyValue, focusElementRef } = props;
  const idBase = item.id ?? "filter";

  return (
    <FormControl size="small" sx={{ minWidth: 120, mt: 0.5 }}>
      <InputLabel id={`select-filter-label-${idBase}`} shrink>Value</InputLabel>
      <Select
        id={`select-filter-${idBase}`}
        labelId={`select-filter-label-${idBase}`}
        inputRef={focusElementRef}
        value={(item.value as string) ?? ""}
        onChange={(event) => applyValue({ ...item, value: event.target.value })}
        variant="standard"
        displayEmpty
      >
        {options.map((option) => (
          <MenuItem key={option.value || "all"} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const createSelectFilterOperator = (
  options: ReadonlyArray<{ label: string; value: string }>
) => ({
  label: "is",
  value: "equals",
  getApplyFilterFn: () => null,
  InputComponent: (props: GridFilterInputValueProps) => (
    <SelectFilterInput props={props} options={options} />
  ),
});

const shipmentMethodFilter = createSelectFilterOperator([
  { label: "All", value: "" },
  ...shipmentMethodOptions.map((value) => ({ label: value, value })),
]);
const shipmentTermFilter = createSelectFilterOperator([
  { label: "All", value: "" },
  ...shipmentTermOptions.map((value) => ({ label: value, value })),
]);
const factoryFilter = createSelectFilterOperator([
  { label: "All", value: "" },
  ...factoryOptions.map((value) => ({ label: value, value })),
]);
const cashmereFilter = createSelectFilterOperator([
  { label: "All", value: "" },
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
]);

const OrderStatusFilterOperator = {
  label: "is",
  value: "equals",
  getApplyFilterFn: ()=>null,
  InputComponent: (props:GridFilterInputValueProps) => {
    const { item, applyValue, focusElementRef } = props;
    const idBase = item.id ?? "filter";
    const labelId = `status-filter-label-${idBase}`;
    const selectId = `status-filter-select-${idBase}`;
  return (
  <FormControl
        size="small"
        className="MuiDataGrid-filterFormOperatorInput"
        sx={{
          minWidth: 120,
          mt: 0.5,
          // COPY MUI operator form style
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            lineHeight: "1.4375em",
            color: "text.secondary",
            transform: "translate(0, -1.5px) scale(0.75)",
            transformOrigin: "top left",
            "&.Mui-focused": {
              color: "primary.main",
            },
          },

          "& .MuiInput-root": {
            // underline variant
            "&:before": {
              borderBottom: "1px solid rgba(0,0,0,0.42)",
            },
            "&:after": {
              borderBottom: "2px solid",
              borderColor: "primary.main",
            },
          },

          "& .MuiSelect-select": {
            paddingRight: "24px !important",
            textOverflow: "ellipsis",
            fontSize: "0.875rem",
          
          },

          "& .MuiSvgIcon-root": {
            transform: "translateY(-2px)",
          },
        }}
      >
        <InputLabel id={labelId} shrink>
          Value
        </InputLabel>

        <Select
          id={selectId}
          labelId={labelId}
          inputRef={focusElementRef}
          value={(item.value as Status) ?? ""}
          onChange={(e) =>
            applyValue({ ...item, value: e.target.value as Status })
          }
          variant="standard" // REQUIRED to match operator styling
          displayEmpty
        >
          <MenuItem value=""></MenuItem>
          {statusOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
 
    );
  },
};

const FactoryInvoicedFilter={
  label: "is",
  value: "equals",
  getApplyFilterFn: ()=>null,
  InputComponent: (props:GridFilterInputValueProps) => {
    const { item, applyValue, focusElementRef } = props;
    const idBase = item.id ?? "filter";
    const labelId = `fact-filter-label-${idBase}`;
    const selectId = `fact-filter-select-${idBase}`;

  return(
    <FormControl
        size="small"
        className="MuiDataGrid-filterFormOperatorInput"
        sx={{
          minWidth: 120,
          mt: 0.5,
          // COPY MUI operator form style
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            lineHeight: "1.4375em",
            color: "text.secondary",
            transform: "translate(0, -1.5px) scale(0.75)",
            transformOrigin: "top left",
            "&.Mui-focused": {
              color: "primary.main",
            },
          },

          "& .MuiInput-root": {
            // underline variant
            "&:before": {
              borderBottom: "1px solid rgba(0,0,0,0.42)",
            },
            "&:after": {
              borderBottom: "2px solid",
              borderColor: "primary.main",
            },
          },

          "& .MuiSelect-select": {
            paddingRight: "24px !important",
            textOverflow: "ellipsis",
            fontSize: "0.875rem",
          
          },

          "& .MuiSvgIcon-root": {
            transform: "translateY(-2px)",
          },
        }}
      >
          <InputLabel id={labelId} shrink>
          Value
        </InputLabel>
                <Select
          id={selectId}
          labelId={labelId}
          inputRef={focusElementRef}
          value={(item.value as Status) ?? ""}
          onChange={(e) =>
            applyValue({ ...item, value: e.target.value as Status })
          }
          variant="standard" // REQUIRED to match operator styling
          displayEmpty
        >
          <MenuItem value=""></MenuItem>
          {factoryOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
       
  )
 },
  

}
export const getSuffix = (row: any) => {
  const baseSuffix = row?.is_sms
    ? "SMS"
    : row?.is_booking
    ? "BOOKING"
    : row?.flexsuffixes
    ? row?.flexsuffixes.code
    : "n/a";

  return baseSuffix + (row?.was_booking ? " (created from booking)" : "");
};

export const orderHeadColumnDef: GridColDef[] = [
  {
    field: "code",
    headerName: "Order ID",
    type: "string",
    flex: 0.8,
    headerAlign: "center",
    align: "left",
    editable: false,
    valueGetter: (_value, row: IOrderHead) => {
      return `${row.code}`;
    },
    renderCell: (params: any) => {

      return(
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
 

      <span
        style={{
          color: colorVariables.primary,
          cursor: "pointer",
        }}
      >
        {params.value}
      </span>
         {params.row.isCashmere && (
            <Tooltip title="Cashmere Item">
              <StarIcon
                sx={{ fontSize: 16, color: "gold" }} // Customize size and color
                aria-label="Cashmere order"
              />
            </Tooltip>
          )}
          {params.row.incompleteInfo && params.row.incompleteInfo.length > 0 && (
             <Tooltip title={`Missing ${params.row?.incompleteInfo?.join(", ")}`}>
              <ErrorOutlineIcon
                sx={{ fontSize: 16, color: "red" }} // Customize size and color
               aria-label={`Missing `}
              />
            </Tooltip>
          )}
        </Box>
      )
    }
  },
  {
    field: "type",
    headerName: "Type",
    type: "string",
    flex: 0.8,
    headerAlign: "left",
    align: "center",
    editable: false,
    valueGetter: (value, row: IBooking) => {
      return `${getSuffix(row)}`;
    },
    renderCell: (params: any) => (
      <span
        style={{
          color: colorVariables.primary,
        }}
      >
        {params.value}
      </span>
    ),
  },
  {
    field: "creationDate",
    headerName: "Creation Date",
    type: "date",
    flex: 1.2,
    align: "center",
    headerAlign: "center",
    editable: false,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      }
      return "n/a";
    },
  },
  {
    field: "client",
    headerName: "Customer Name",
    type: "string",
    flex: 1,
    align: "left",
    headerAlign: "center",
    editable: true,
    valueGetter: (_value, row: IOrderHead) => {
      return `${row.client?.customer_name ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderCustomer {...params} />,
  },
  {
    field: "orderstatus",
    headerName: "Status",
    type: "string",
    flex: 0.6,
    align: "center",
    headerAlign: "center",
    editable: false,
    renderCell: (params: any) => (
      <span
        style={{
          fontWeight: "bold",
          color:
            params.value === EORDERHEAD_STATUS.NEW
              ? colorVariables.darkGreen
              : params.value === EORDERHEAD_STATUS.SHIPPED
              ? colorVariables.blue
              : params.value === EORDERHEAD_STATUS.ALLOCATED
              ? colorVariables.orange
              : colorVariables.red,
        }}
      >
        {params.value?.toUpperCase() ?? "n/a"}
      </span>
    ),
    renderEditCell: (params) => <RenderStatus {...params} />,
    filterOperators: [OrderStatusFilterOperator],

  },
  {
    field: "merchandiser",
    headerName: "Merchandiser",
    type: "string",
    flex: 0.5,
    align: "left",
    headerAlign: "center",
    editable: true,
    valueGetter: (_value, row: IOrderHead) => {
      return `${row.merchandiser?.description ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderMerchandiser {...params} />,
  },
  {
    field: "salespeople",
    headerName: "Sales Person",
    type: "string",
    flex: 0.5,
    align: "left",
    headerAlign: "center",
    editable: true,
    valueGetter: (_value, row: IOrderHead) => {
      return `${row.salespeople?.name ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderSalesPeople {...params} />,
  },
  {
    field: "expectedOrderDate",
    headerName: "Exp. Order Date",
    type: "date",
    flex: 1.5,
    align: "center",
    headerAlign: "center",
    editable: true,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      }
      return "n/a";
    },
  },
  {
    field: "shipmentMethod",
    headerName: "Shipment Method",
    type: "string",
    flex: 0.5,
    headerAlign: "center",
    align: "center",
    editable: true,
    sortable: false,
    valueGetter: (_value, row: IOrderHead) => {
      return row.shipmentMethod ? row.shipmentMethod : "n/a";
    },
    renderEditCell: (params) => <RenderShipmentMode {...params} />,
    filterOperators: [shipmentMethodFilter],
  },
  {
    field: "shipmentTerms",
    headerName: "Shipment Terms",
    type: "string",
    flex: 0.5,
    headerAlign: "center",
    align: "center",
    editable: true,
    sortable: false,
    valueGetter: (_value, row: IOrderHead) => {
      return row.shipmentTerms ? row.shipmentTerms : "n/a";
    },
    renderEditCell: (params) => <RenderShipmentTerm {...params} />,
    filterOperators: [shipmentTermFilter],
  },
  {
    field: "factory",
    headerName: "Factory",
    type: "string",
    flex: 1,
    headerAlign: "center",
    align: "center",
    editable: false,
    sortable: false,
    valueGetter: (_value, row: IOrderHead) => {
      return row.factory ? row.factory : "n/a";
    },
    renderEditCell: (params) => <RenderFactory {...params} />,
    filterOperators: [factoryFilter],
  },
  {
    field: "invoice",
    headerName: "Invoice",
    type: "string",
    flex: 0.5,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueGetter: (_value, row: IOrderHead) => {
      return row.invoice ? row.invoice.code : "n/a";
    },
    renderEditCell: (params) => <RenderInvoice {...params} />,
    filterOperators: [FactoryInvoicedFilter],
  },
  {
    field: "totalQuantity",
    headerName: "Total Quantity",
    align: "right",
    headerAlign: "right",
    type: "number",
    flex: 1,
    filterable: false,
    sortable: false,
    editable: false,
  },
  {
    field: "totalValue",
    headerName: "Total price",
    type: "number",
    flex: 1,
    align: "right",
    headerAlign: "right",
    editable: false,
    filterable: false,
    sortable: false,
    valueGetter: (_value, row: IOrderHead) => {
      return `${numeral(row.totalValue).format("0,0") ?? 0} ${
        row.currency?.code ?? ""
      }`;
    },
  },
];

export const orderStyleColumnDef: GridColDef[] = [
  {
    field: "order_id",
    headerName: "Order ID",
    type: "string",
    flex: 0.7,
    headerAlign: "left",
    align: "left",
    editable: false,
    renderCell: (params: any) => (
      <span style={{ color: colorVariables.primary, cursor: "pointer" }}>
        {params.value}
      </span>
    ),
  },
  {
    field: "style_code",
    headerName: "Style Code",
    type: "string",
    flex: 1,
    headerAlign: "left",
    align: "left",
    editable: false,
  },
  {
    field: "style_description",
    headerName: "Style Description",
    type: "string",
    flex: 2.5,
    headerAlign: "left",
    align: "left",
    editable: false,
    renderCell: (params: any) => (
      <span style={{ color: colorVariables.primary, cursor: "pointer" }}>
        {params.value}
      </span>
    ),
  },
  {
    field: "created_at",
    headerName: "Creation Date",
    type: "date",
    flex: 1.2,
    align: "center",
    headerAlign: "center",
    editable: false,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      }
      return "n/a";
    },
  },
  {
    field: "customer_name",
    headerName: "Customer name",
    type: "string",
    flex: 1,
    align: "left",
    headerAlign: "left",
    editable: true,
    valueGetter: (_value, row: IOrderStyle) => {
      return `${row.orderhead?.client?.customer_name ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderCustomer {...params} />,
  },
  {
    field: "machine_type",
    headerName: "Machine Type",
    type: "string",
    flex: 0.8,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueGetter: (_value, row: IOrderStyle) => {
      return `${row.machine_type ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderMachine {...params} />,
  },
  {
    field: "factory",
    headerName: "Factory Type",
    type: "string",
    flex: 0.8,
    headerAlign: "center",
    align: "center",
    editable: false,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Box>
        <span style={{ fontWeight: "bold" }}>
          {params.row.factory1 && "F1"}{" "}
        </span>
        {params.row.factory1} -{" "}
        <span style={{ fontWeight: "bold" }}>
          {params.row.factory2 && "F2"}{" "}
        </span>
        {params.row.factory2}
      </Box>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    type: "string",
    flex: 0.8,
    align: "center",
    headerAlign: "center",
    editable: false,
    renderCell: (params: any) => (
      <span
        style={{
          fontWeight: "bold",
          color:
            params.value === EORDERHEAD_STATUS.NEW
              ? colorVariables.darkGreen
              : params.value === EORDERHEAD_STATUS.SHIPPED
              ? colorVariables.blue
              : params.value === EORDERHEAD_STATUS.ALLOCATED
              ? colorVariables.orange
              : colorVariables.red,
        }}
      >
        {params.value?.toUpperCase() ?? ""}
      </span>
    ),
  },
];

export const bookingColumnDef: GridColDef[] = [
  {
    field: "code",
    headerName: "Order ID",
    type: "string",
    flex: 0.25,
    headerAlign: "left",
    align: "left",
    editable: false,
    valueGetter: (value, row: IBooking) => {
      return `${row.code ?? "n/a"}`;
    },
    renderCell: (params: any) => (
      <span
        style={{
          color: colorVariables.primary,
          cursor: "pointer",
        }}
      >
        {params.value}
      </span>
    ),
    renderEditCell: (params: any) => (
      <span
        style={{
          fontWeight: "bold",
        }}
      >
        {params.value}
      </span>
    ),
  },
  {
    field: "client",
    headerName: "Customer name",
    type: "string",
    flex: 0.4,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueGetter: (_value, row: IBooking) => {
      return `${row.client?.customer_name ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderCustomer {...params} />,
  },
  {
    field: "merchandiser",
    headerName: "Merchandiser",
    type: "string",
    flex: 0.4,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueGetter: (_value, row: IBooking) => {
      return `${row.merchandiser?.description ?? "n/a"}`;
    },
    renderEditCell: (params) => <RenderMerchandiser {...params} />,
  },
  {
    field: "orderstatus",
    headerName: "Status",
    type: "string",
    flex: 0.4,
    headerAlign: "center",
    align: "center",
    editable: true,
    renderCell: (params: any) => (
      <span
        style={{
          fontWeight: "bold",
          color:
            params.value === EORDERHEAD_STATUS.NEW
              ? colorVariables.darkGreen
              : params.value === EORDERHEAD_STATUS.SHIPPED
              ? colorVariables.blue
              : params.value === EORDERHEAD_STATUS.ALLOCATED
              ? colorVariables.orange
              : colorVariables.red,
        }}
      >
        {params.value?.toUpperCase() ?? "n/a"}
      </span>
    ),
    renderEditCell: (params) => <RenderStatus {...params} />,
  },
  {
    field: "expectedOrderDate",
    headerName: "Expected Order Date",
    type: "date",
    flex: 0.3,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      }
      return "n/a";
    },
  },
  {
    field: "creationDate",
    headerName: "Created at",
    type: "date",
    flex: 0.3,
    headerAlign: "center",
    align: "center",
    editable: true,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY HH:mm");
      }
      return "n/a";
    },
  },
  {
    field: "shipmentMethod",
    headerName: "Shipment Method",
    type: "string",
    flex: 0.35,
    headerAlign: "center",
    align: "center",
    sortable: false,
    valueGetter: (_value, row: IBooking) => row.shipmentMethod || "n/a",
    filterOperators: [shipmentMethodFilter],
  },
  {
    field: "shipmentTerms",
    headerName: "Shipment Terms",
    type: "string",
    flex: 0.35,
    headerAlign: "center",
    align: "center",
    sortable: false,
    valueGetter: (_value, row: IBooking) => row.shipmentTerms || "n/a",
    filterOperators: [shipmentTermFilter],
  },
  {
    field: "factory",
    headerName: "Factory",
    type: "string",
    flex: 0.35,
    headerAlign: "center",
    align: "center",
    sortable: false,
    valueGetter: (_value, row: IBooking) => row.factory || "n/a",
    filterOperators: [factoryFilter],
  },
  {
    field: "isCashmere",
    headerName: "Cashmere",
    type: "string",
    flex: 0.3,
    headerAlign: "center",
    align: "center",
    sortable: false,
    valueGetter: (_value, row: IBooking) => row.isCashmere ? "Yes" : "No",
    filterOperators: [cashmereFilter],
  },
];
